import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Gift, BookOpen, Printer, Settings } from "lucide-react";
import { SimplePage } from "@/components/SimplePage";
import { AcornJar } from "@/components/AcornJar";
import { useFamilyProfile } from "@/hooks/useFamilyProfile";
import { useAcornJar } from "@/hooks/useAcornJar";
import { getRewards, getAwards, awardPhotoUrl } from "@/lib/rewards";
import { RewardConfetti } from "@/components/RewardConfetti";
import { getGolden, SOURCE_LABEL } from "@/lib/golden";

const Progress = ({ have, need, gold }) => (
  <div className={`pp-progress ${gold ? "gold" : ""}`}>
    <span style={{ width: `${need ? Math.min(100, (have / need) * 100) : 100}%` }} />
  </div>
);

const RewardCard = ({ r, saved, name }) => {
  const ready = saved.acorns >= r.acorns && saved.golden >= r.golden;
  return (
    <li className={`pp-card pp-reward-card ${ready ? "ready" : ""}`} data-testid={`reward-card-${r.id}`}>
      <h3 className="pp-h2 flex items-center gap-2"><Gift className="h-5 w-5" /> {r.title}</h3>
      <Link to={`/print/reward-chart?reward=${encodeURIComponent(r.title)}&acorns=${r.acorns}&golden=${r.golden}${name ? `&name=${encodeURIComponent(name)}` : ""}`}
        target="_blank" className="pp-ghost pp-chart-link" data-testid={`reward-chart-${r.id}`}><Printer className="h-4 w-4" /> Print chart</Link>
      {r.acorns > 0 && <><p className="text-sm mt-2">{Math.min(saved.acorns, r.acorns)} / {r.acorns} acorns</p><Progress have={saved.acorns} need={r.acorns} /></>}
      {r.golden > 0 && <><p className="text-sm mt-2">{Math.min(saved.golden, r.golden)} / {r.golden} golden acorns</p><Progress have={saved.golden} need={r.golden} gold /></>}
      {ready && <p className="pp-reward gold mt-3" data-testid={`reward-ready-${r.id}`}>Ready! Ask your grown-up.</p>}
    </li>
  );
};

export default function Rewards() {
  const { profile } = useFamilyProfile();
  const { jar } = useAcornJar(profile);
  const [data, setData] = useState(null);
  const [awards, setAwards] = useState([]);
  const [golden, setGolden] = useState([]);
  useEffect(() => {
    getRewards(profile?.id).then(setData);
    if (profile) getAwards(profile.id).then(setAwards);
    if (profile) getGolden(profile.id).then((d) => setGolden(d.items)).catch(() => setGolden([]));
    else setGolden([]);
  }, [profile]);
  const saved = data?.saved || { acorns: 0, golden: 0 };
  const [party, setParty] = useState(null);
  useEffect(() => {
    if (!profile || !data?.saved) return;
    const key = `pp_celebrated_${profile.id}`;
    const seen = JSON.parse(localStorage.getItem(key) || "[]");
    const ready = data.rewards.filter((r) => data.saved.acorns >= r.acorns && data.saved.golden >= r.golden);
    const fresh = ready.find((r) => !seen.includes(r.id));
    localStorage.setItem(key, JSON.stringify(ready.map((r) => r.id)));
    if (fresh) setParty(fresh);
  }, [data, profile]);

  return (
    <SimplePage testId="rewards-page">
      {party && <RewardConfetti reward={party} name={profile?.name} onClose={() => setParty(null)} />}
      <p className="pp-label">{profile ? `${profile.name}'s rewards` : "Family rewards"}</p>
      <h1 className="pp-h1">Work toward a reward</h1>
      <p className="pp-sub max-w-xl">Earn acorns for kind words and golden acorns for gratitude and great manners in real life. Your grown-ups choose the rewards.</p>
      <div className="flex flex-wrap gap-3 items-center mt-6">
        <AcornJar jar={jar} testId="rewards-jar" />
        {profile && <span className="pp-badge" data-testid="rewards-saved">Saved: {saved.acorns} {saved.acorns === 1 ? "acorn" : "acorns"} · {saved.golden} golden</span>}
        <Link to={`/print/reward-chart${profile ? `?name=${encodeURIComponent(profile.name)}` : ""}`} target="_blank" className="pp-ghost" data-testid="rewards-blank-chart-link"><Printer className="h-4 w-4" /> Print a 30-acorn chart</Link>
        <Link to="/family#rewards" className="pp-ghost" data-testid="rewards-create-own-link"><Settings className="h-4 w-4" /> Parents: create your own rewards</Link>
        <Link to="/golden-acorns" className="pp-ghost" data-testid="rewards-guide-link"><BookOpen className="h-4 w-4" /> How golden acorns work</Link>
      </div>
      {!profile && <p className="pp-feedback small mt-6" data-testid="rewards-pick-profile">Pick your name from the profile menu on the home page to see your progress.</p>}
      <ul className="pp-rewards-grid mt-8" data-testid="rewards-list">
        {data?.rewards.map((r) => <RewardCard key={r.id} r={r} saved={saved} name={profile?.name} />)}
      </ul>
      {golden.length > 0 && (
        <section className="pp-card mt-8" data-testid="rewards-golden-collection">
          <h2 className="pp-h2 flex items-center gap-2"><Award className="h-5 w-5 text-amber-500" /> My Golden Acorn Collection</h2>
          <p className="opacity-75 mt-1">Every time you told someone what you appreciated.</p>
          <ul className="mt-4 grid gap-2">
            {golden.map((g) => (
              <li key={g.id} className="pp-award-row" data-testid={`rewards-golden-${g.id}`}>
                <img src="/acorn-t.png" alt="" /> <div><b>{g.skill}</b> <span className="text-sm opacity-70">· {SOURCE_LABEL[g.source]}</span>
                  <p className="text-sm opacity-80">{g.quote ? `“${g.quote}”` : g.category}</p></div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {awards.length > 0 && (
        <section className="pp-card mt-8" data-testid="rewards-awards">
          <h2 className="pp-h2 flex items-center gap-2"><Award className="h-5 w-5 text-amber-500" /> Golden acorns from your grown-ups</h2>
          <ul className="mt-4 grid gap-2">
            {awards.map((a) => (
              <li key={a.id} className="pp-award-row" data-testid={`award-row-${a.id}`}>
                <img src="/acorn-t.png" alt="" /> <div><b>{a.reason}</b>{a.note && <p className="text-sm opacity-80">“{a.note}”</p>}
                  {a.photo && (
                    <a href={awardPhotoUrl(a.id)} target="_blank" rel="noopener noreferrer" data-testid={`award-photo-${a.id}`}>
                      <img className="pp-award-photo" src={awardPhotoUrl(a.id)} alt="Note from teacher" loading="lazy" />
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </SimplePage>
  );
}
